import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { fade, openClose } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import { getJurisdictionAndDepartmentByComplianceRequest } from '@core/util/jurisdictions';
import { replaceByCollectionContext, replaceGroupId } from '@core/util/string';
import { ComplianceRequest, Jurisdiction } from '@models/jurisdictions';
import {
	MatterStatementOfAccount,
	StatementOfAccountDefaultTypes,
	StatementOfAccountType,
} from '@models/statementOfAccount';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { TagsSelector } from '@state/global/tags/tags.selector';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { StatementOfAccountsDispatch } from '@state/matter/statementOfAccounts';
import { cloneDeep } from 'lodash';
import { Observable, Subject, Subscription, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { StatementOfAccountTotals } from '../../../../shared/services/financialTotals/financial-totals.service';
import { SummaryDocketComponent } from '../../teranet/summary-docket/summary-docket.component';

export interface ReorderStatement {
	id: string;
	index: number;
}

const DisabledSOATypes = [
	StatementOfAccountDefaultTypes.TITLE_INSURANCE_PREMIUM,
	StatementOfAccountDefaultTypes.TITLE_INSURANCE_CREDIT,
	StatementOfAccountDefaultTypes.LTT,
	StatementOfAccountDefaultTypes.MLTT,
	StatementOfAccountDefaultTypes.LTT_AND_MLTT,
	StatementOfAccountDefaultTypes.MLTT_ADMIN_FEE,
];

const ViewDocketTypes = [
	StatementOfAccountDefaultTypes.HST_TITLE,
	StatementOfAccountDefaultTypes.HST_WRIT,
	StatementOfAccountDefaultTypes.NON_HST_TITLE,
	StatementOfAccountDefaultTypes.NON_HST_WRIT,
];

@UntilDestroy()
@Component({
	selector: 'app-statement-of-account-table',
	templateUrl: 'statement-of-account-table.component.html',
	styleUrls: ['statement-of-account-table.component.scss'],
	animations: [openClose(), fade()],
	standalone: false,
})
export class StatementOfAccountTableComponent {
	public statementTypes = StatementOfAccountType;
	public viewDocketTypes = ViewDocketTypes;
	public ICON = ICON;
	public statementId: (index, statement: MatterStatementOfAccount) => string;
	public _statements: MatterStatementOfAccount[];
	public deletedStatements: MatterStatementOfAccount[];
	public feeDefaultType = StatementOfAccountDefaultTypes.FEE_ACTING_ON;
	public disabledTypes = DisabledSOATypes;
	public showDeleted = false;

	private highlightedEventStream = new Subject<string>();
	highlightedStatement: string = null;

	@ViewSelectSnapshot(SelectedMatter.statementOfAccountUseAllInclusivePrice)
	useAllInclusivePrice;
	@Select(TagsSelector.getTagInjectedDocument)
	parsedDocumentFilterFn$: Observable<(doc: string) => string>;
	@Select(MatterJurisdictionSelectors.getAll) jurisdictions$: Observable<
		Jurisdiction[]
	>;
	@Select(MatterJurisdictionSelectors.getAllComplianceRequests)
	complianceRequests$: Observable<ComplianceRequest[]>;
	@Select(TagsSelector.getTags) tags$: Observable<string[]>;

	@Output() public addStatement: EventEmitter<void>;
	@Output() public editStatementText: EventEmitter<MatterStatementOfAccount>;
	@Input() hst: number;

	public statementOfAccountDescriptionParserSubcription: Subscription;

	@Input() public set statements(statements: MatterStatementOfAccount[]) {
		this.statementOfAccountDescriptionParserSubcription?.unsubscribe();
		// Todo: Refactor this to use context and not refId, this is a temporary patch only. Simon agreeded to it.
		this.statementOfAccountDescriptionParserSubcription = combineLatest([
			this.jurisdictions$,
			this.complianceRequests$,
			this.tags$,
		])
			.pipe(untilDestroyed(this))
			.subscribe(([jurisdictions, complianceRequests]) => {
				const _statements = cloneDeep(statements)?.map((x) => {
					const { jurisdiction, department } =
						x.defaultType ===
						StatementOfAccountDefaultTypes.COMPLIANCE_REQUEST_FEE
							? getJurisdictionAndDepartmentByComplianceRequest(
									jurisdictions,
									complianceRequests.find((y) => y.id === x.refId),
								)
							: { department: null, jurisdiction: null };
					const { description } = x;

					const parsedDescription = this.getParsedDescription(
						(x.defaultType ===
							StatementOfAccountDefaultTypes.COMPLIANCE_REQUEST_FEE &&
						jurisdictions.length > 0 &&
						complianceRequests.length > 0
							? replaceByCollectionContext(description, {
									complianceRequests: [{ referenceId: x?.refId }],
									jurisdictions: [{ referenceId: jurisdiction?.id }],
									departments: [{ referenceId: department?.id }],
								})
							: replaceGroupId(description, x.refId)) as string,
					);

					return {
						...x,
						parsedDescription: parsedDescription,
					};
				});

				this._statements = _statements?.filter((x) => !x.deleted) ?? [];
				this.deletedStatements = _statements?.filter((x) => x.deleted) ?? [];
			});
	}

	@Input() public statementType: StatementOfAccountType;
	@Input() public title: string;
	@Input() public totals: StatementOfAccountTotals;
	@Input() public tabIndexStart = 0;

	constructor(
		private statementDispatch: StatementOfAccountsDispatch,
		private matDialog: MatDialog,
	) {
		this.addStatement = new EventEmitter<void>();
		this.editStatementText = new EventEmitter<MatterStatementOfAccount>();
		this.statementId = (index, statement: MatterStatementOfAccount): string =>
			statement.id;
	}

	add(): void {
		this.addStatement.emit();
	}

	delete(statement: MatterStatementOfAccount): void {
		statement.deleted = true;
		this.statementDispatch.SetStatement(statement.id, statement);
	}

	addBack(statement: MatterStatementOfAccount): void {
		this.statementDispatch.SetStatement(statement.id, {
			deleted: false,
		} as MatterStatementOfAccount);
	}

	drop(event: CdkDragDrop<string[]>): void {
		const reOrderedStatements = cloneDeep(this._statements);
		moveItemInArray(
			reOrderedStatements,
			event.previousIndex,
			event.currentIndex,
		);
		this._statements = reOrderedStatements;
		this.statementDispatch.SetOrder(reOrderedStatements);
	}

	edit(id: string): void {
		this.editStatementText.emit(
			this._statements.find((statement) => statement.id === id),
		);
	}

	setPrice(id: string, value: number): void {
		const statement = this._statements.find((x) => x.id === id);
		statement.price = value ?? 0;

		this.statementDispatch.SetStatement(id, statement);
	}

	viewDocket(statement: MatterStatementOfAccount): void {
		this.matDialog.open(SummaryDocketComponent, {
			width: '70vw',
			height: '80vh',
			data: {
				statement,
			},
		});
	}

	getParsedDescription(description: string): string {
		let parsedDescription: string = description;
		this.getParsedDocument$(cloneDeep(description))
			.pipe(take(1))
			.subscribe((result) => {
				parsedDescription = result;

				return parsedDescription;
			});

		return parsedDescription;
	}

	getParsedDocument$(document: string) {
		return this.parsedDocumentFilterFn$.pipe(
			untilDestroyed(this),
			map((filter) => filter(document)),
		);
	}
}
